import React from 'react';
import { Input, Switch, Space, Typography, Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { customizationRulesSchema } from '../validation';
import { useTheme } from '../../../../context/ThemeContext';
import {
  StyledCard,
  CursorPointerIcon,
  CursorPointerText,
  TitleNoMargin,
  FullWidthSpaceLarge,
  HelperTextContainer,
  SecondaryTextSmall,
  InputNumberFullWidth,
  SubCard,
} from '../Styles';
import { CustomizationRulesProps, FormValues } from '../types';
import { SUBMISSION_TYPES } from '../constants';

const { Text } = Typography;

const CustomizationRules: React.FC<CustomizationRulesProps> = ({
  customizationData,
  handleBackClick,
  handleSubmit,
  width,
}) => {
  const {
    control,
    handleSubmit: rhfSubmit,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(customizationRulesSchema) as any,
    mode: 'onChange',
    defaultValues: {
      quantityEnabled: !!customizationData.enable_quantity_control,
      minQuantity: customizationData.min_quantity ?? null,
      maxQuantity: customizationData.max_quantity ?? null,
      multipleSelections: customizationData.multiple_selections || false,
      multipleSelectionsQuantity: customizationData.multiple_selections_quantity ?? null,
      note: customizationData.note || '',
    },
  });

  const quantityEnabled = watch('quantityEnabled');
  const multipleSelections = watch('multipleSelections');

  const onSubmit = async (values: FormValues) => {
    const updatedCustomizationData = {
      ...customizationData,
      enable_quantity_control: values.quantityEnabled,
      min_quantity: values.quantityEnabled ? values.minQuantity : null,
      max_quantity: values.quantityEnabled ? values.maxQuantity : null,
      multiple_selections: values.multipleSelections,
      multiple_selections_quantity: values.multipleSelections ? values.multipleSelectionsQuantity : null,
      note: values.note,
    };
    await handleSubmit(SUBMISSION_TYPES.CUSTOMIZATION_RULES, updatedCustomizationData);
  };

  const { isDarkMode } = useTheme();
  const isFixed = !width;

  const labelColor = isDarkMode ? '#e5e7eb' : undefined;

  return (
    <StyledCard $width={width} $isFixed={isFixed}>
      <div style={{ flexShrink: 0, padding: 16 }}>
        <Space>
          <CursorPointerIcon onClick={handleBackClick}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </CursorPointerIcon>
          <CursorPointerText onClick={handleBackClick}>
            Customizations
          </CursorPointerText>
          <Text> / Customization rules</Text>
        </Space>
        <TitleNoMargin level={4} style={{ marginTop: 12, marginBottom: 4 }}>Customisation rules</TitleNoMargin>
        <Text type="secondary">Set limits for customising items</Text>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px', minHeight: 0 }}>
        <FullWidthSpaceLarge direction="vertical" size="large" style={{ marginTop: 16 }}>

          {/* Quantity per option */}
          <div>
            <Text strong>Quantity per option</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                Limit how many units of a single option a customer can add (e.g. max 3 servings of fries)
              </SecondaryTextSmall>
            </HelperTextContainer>
            <Controller
              name="quantityEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {quantityEnabled && (
              <SubCard $isDark={isDarkMode}>
                <div>
                  <Text strong style={{ color: labelColor }}>
                    Minimum quantity <Text type="danger">*</Text>
                  </Text>
                  <HelperTextContainer>
                    <SecondaryTextSmall type="secondary">
                      Minimum units of a single option the customer must add
                    </SecondaryTextSmall>
                  </HelperTextContainer>
                  <Controller
                    name="minQuantity"
                    control={control}
                    render={({ field }) => (
                      <InputNumberFullWidth
                        value={field.value}
                        placeholder="e.g. 1"
                        status={errors.minQuantity ? 'error' : ''}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(typeof value === 'number' ? value : null);
                          trigger('maxQuantity');
                        }}
                      />
                    )}
                  />
                  {errors.minQuantity && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {errors.minQuantity.message}
                    </Text>
                  )}
                </div>
                <div>
                  <Text strong style={{ color: labelColor }}>
                    Maximum quantity <Text type="danger">*</Text>
                  </Text>
                  <HelperTextContainer>
                    <SecondaryTextSmall type="secondary">
                      Maximum units of a single option the customer can add
                    </SecondaryTextSmall>
                  </HelperTextContainer>
                  <Controller
                    name="maxQuantity"
                    control={control}
                    render={({ field }) => (
                      <InputNumberFullWidth
                        value={field.value}
                        placeholder="e.g. 5"
                        status={errors.maxQuantity ? 'error' : ''}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(typeof value === 'number' ? value : null);
                        }}
                      />
                    )}
                  />
                  {errors.maxQuantity && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {errors.maxQuantity.message}
                    </Text>
                  )}
                </div>
              </SubCard>
            )}
          </div>

          {/* Multiple selections */}
          <div>
            <Text strong>Multiple selections</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                Allow customers to pick more than one option from this list (e.g. choose 3 toppings out of 5)
              </SecondaryTextSmall>
            </HelperTextContainer>
            <Controller
              name="multipleSelections"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {multipleSelections && (
              <SubCard $isDark={isDarkMode} style={{ gap: 0 }}>
                <Text strong style={{ color: labelColor }}>
                  Maximum options a customer can select <Text type="danger">*</Text>
                </Text>
                <HelperTextContainer>
                  <SecondaryTextSmall type="secondary">
                    How many different options can a customer choose from this list? (e.g. pick any 3 out of 5 toppings)
                  </SecondaryTextSmall>
                </HelperTextContainer>
                <Controller
                  name="multipleSelectionsQuantity"
                  control={control}
                  render={({ field }) => (
                    <InputNumberFullWidth
                      value={field.value}
                      placeholder="e.g. 3"
                      status={errors.multipleSelectionsQuantity ? 'error' : ''}
                      onBlur={field.onBlur}
                      onChange={(value) => {
                        field.onChange(typeof value === 'number' ? value : null);
                      }}
                    />
                  )}
                />
                {errors.multipleSelectionsQuantity && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    {errors.multipleSelectionsQuantity.message}
                  </Text>
                )}
              </SubCard>
            )}
          </div>

          {/* Note */}
          <div>
            <Text strong>Note</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                Add any additional notes or instructions for this customization
              </SecondaryTextSmall>
            </HelperTextContainer>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter notes or instructions"
                />
              )}
            />
          </div>
        </FullWidthSpaceLarge>
      </div>

      {/* Pinned footer */}
      <div style={{ flexShrink: 0, padding: '12px 16px 16px', borderTop: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`, display: 'flex', justifyContent: 'flex-end', background: isDarkMode ? '#1a1a2e' : '#fff' }}>
        <Button
          type="primary"
          onClick={rhfSubmit(onSubmit)}
          loading={isSubmitting}
          disabled={isSubmitting}
          style={{ height: 40, paddingInline: 24, backgroundColor: '#2D6CCA', borderColor: '#2D6CCA' }}
        >
          Save
        </Button>
      </div>
    </StyledCard>
  );
};

export default CustomizationRules;
